import React, { useCallback, useEffect, useMemo, useState } from "react";
import MapHeroSection from "./MapHeroSection/MapHeroSection";
import BusLoader from "../Loader/BusLoader";
import { useSocket } from "../../hooks/useSocket";
import {
  mergeTripsById,
  normalizeTrips,
  searchTrips,
  VIEW_MODES,
} from "../../utils/tripUtils";
import "./LandingPage.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const LandingPage = ({ dedicatedTripId = null }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [scheduledTrips, setScheduledTrips] = useState([]);
  const [query, setQuery] = useState({ from: "", to: "", text: "", type: "all" });
  const [mode, setMode] = useState(
    dedicatedTripId ? VIEW_MODES.DEDICATED_TRACKING : VIEW_MODES.ALL_TRIPS,
  );
  const [selectedTripId, setSelectedTripId] = useState(dedicatedTripId);
  const [apiError, setApiError] = useState(null);

  const socket = useSocket({ mode, selectedTripId });
  const { requestAllTrips, trackTrip } = socket;

  useEffect(() => {
    let cancelled = false;

    const initializeApp = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/api/v1/himraahi/trips`);
        const payload = await response.json();
        if (!cancelled) {
          setScheduledTrips(normalizeTrips(payload?.data || []));
          setApiError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setApiError(error?.message || "Could not load scheduled trips");
        }
      } finally {
        if (!cancelled) {
          window.setTimeout(() => setIsLoading(false), 550);
        }
      }
    };

    initializeApp();

    return () => {
      cancelled = true;
    };
  }, []);

  const liveTrips = normalizeTrips(socket.activeTrips);

  const allTrips = useMemo(() => {
    const merged = mergeTripsById(scheduledTrips, liveTrips);
    return Object.values(merged);
  }, [liveTrips, scheduledTrips]);

  const filteredTrips = useMemo(() => searchTrips(allTrips, query), [allTrips, query]);

  const selectedTrip = useMemo(
    () => allTrips.find((trip) => trip._id === selectedTripId) || null,
    [allTrips, selectedTripId],
  );

  const hasSearch =
    query.from.trim() || query.to.trim() || query.text.trim() || query.type !== "all";

  const visibleTrips =
    mode === VIEW_MODES.SELECTED_TRIP || mode === VIEW_MODES.DEDICATED_TRACKING
      ? selectedTrip
        ? [selectedTrip]
        : filteredTrips
      : hasSearch
      ? filteredTrips
      : allTrips;

  const handleSearchChange = useCallback((nextQuery) => {
    setQuery(nextQuery);
    const nextHasSearch =
      nextQuery.from.trim() ||
      nextQuery.to.trim() ||
      nextQuery.text.trim() ||
      nextQuery.type !== "all";
    if (mode !== VIEW_MODES.DEDICATED_TRACKING) {
      setMode(nextHasSearch ? VIEW_MODES.SEARCH_RESULTS : VIEW_MODES.ALL_TRIPS);
      if (!nextHasSearch) setSelectedTripId(null);
    }
  }, [mode]);

  const handleSelectTrip = useCallback((tripId) => {
    setSelectedTripId(tripId);
    setMode(dedicatedTripId ? VIEW_MODES.DEDICATED_TRACKING : VIEW_MODES.SELECTED_TRIP);
    trackTrip(tripId);
  }, [dedicatedTripId, trackTrip]);

  const handleViewAll = useCallback(() => {
    setQuery({ from: "", to: "", text: "", type: "all" });
    setSelectedTripId(null);
    setMode(VIEW_MODES.ALL_TRIPS);
    requestAllTrips();
  }, [requestAllTrips]);

  if (isLoading) {
    return <BusLoader message="Preparing live map..." />;
  }

  return (
    <div className="landing-page-v2">
      <main className="main-content">
        <MapHeroSection
          allTrips={allTrips}
          visibleTrips={visibleTrips}
          selectedTrip={selectedTrip}
          selectedTripId={selectedTripId}
          query={query}
          mode={mode}
          socketState={socket.connectionState}
          socketError={socket.error}
          apiError={apiError}
          lastSyncedAt={socket.lastSyncedAt}
          onSearchChange={handleSearchChange}
          onSelectTrip={handleSelectTrip}
          onViewAll={handleViewAll}
        />
      </main>
    </div>
  );
};

export default LandingPage;
